import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { EventShowcase, Sankey } from '@src/pages/journey-map/components';
import { Button, Segmented, Spin } from 'antd';
import { generateSankeyData } from './utils/generateSankeyData';
import getSankeySize from './utils/getSankeySize';
import { DEFAULT_TOP_PATHS, useJourneyStore, useAppliedConfig, useDraftConfig } from './store';
import GlobalFilter from '@src/features/GlobalFilter';
import { getShareConfig } from '@src/services/apis/share';
import {
  LeftPanel,
  PanelHeader,
  PanelHeaderForLeft,
  HeaderContainer,
  PageContainer,
  SelectedEventsList,
  SelectedEventTag,
  ChartContainer,
  TopBarOfPatterns,
  BusinessGoal,
  ChartContentContainer,
  FloatPanel,
} from './styled';
import MiniFunnel from '@src/features/MiniFunnel';
import { appTracker } from '@src/shared/utils/sensor/apptracker';
import NoData from '@src/shared/components/NoData';
import { useCommonInfoStore, useDatasetStore } from '@src/shared/store';
import ResizablePanel from '@src/shared/components/ResizablePanel';
import FunnelList from '@src/features/FunnelList';
import MetricChart from '@src/features/MetricChart';
import GenerateJourneyModal from './components/GenerateJourneyModal';
import LeftFilters from './components/LeftFilters';
import { useGetPatterns, usePostUserJourneysFilter } from '@src/services/query/applang/queryHooks';
import type { UserJourneyFilterRequest } from '@src/shared/types/user-journeys';
import useMilestonesEvents from '@src/shared/hooks/useMilestonesEvents';
import { type PatternsRequest } from '@src/shared/types/patterns';
import DatePicker from '@src/shared/components/DatePicker';
import { TIMEOUT_COEFFICIENTS } from '@src/shared/constants/analysis';
import { DisplayType } from './store';
import { CaretRightOutlined, DeleteOutlined, RedoOutlined } from '@ant-design/icons';
import SharePopover from './components/SharePopover';
import { isEqual } from 'lodash';
import { DEFAULT_CONFIG } from './store';
interface IProps {
  highlightedNodes?: string[];
  onNodeLabelClick?: (node: any) => void;
}

const JourneyPage: React.FC<IProps> = ({
  highlightedNodes: highlightedNodesFromProps,
  onNodeLabelClick,
}) => {
  const { state } = useLocation() as { state: { milestone?: string } };

  // Applied config (used for queries)
  const applied = useAppliedConfig();
  // Draft config (user edits before clicking Apply)
  const draft = useDraftConfig();

  const {
    selectedNodes,
    setSelectedNodes,
    globalFilter,
    setGlobalFilter,
    showGenerateJourneyModal,
    setShowGenerateJourneyModal,
    displayType,
    setDisplayType,
    interval,
    setInterval,
    resetAllData: resetStore,
    updateDraft,
    applyConfig,
    hasChanges,
  } = useJourneyStore();

  // Check if there are unsaved changes
  const changed = hasChanges();

  const handleApply = () => {
    applyConfig();

    appTracker({
      name: 'Journey Explorer Apply Config',
      data: {
        businessGoal: draft.businessGoal,
        startEvents: draft.startEvents,
        topPaths: draft.topPaths,
      },
    });
  };
  const { setPaths, patterns, setPatterns } = useDatasetStore();
  const [sankeyAlign, setSankeyAlign] = useState<'justify' | 'center' | 'left' | 'right'>(
    'justify',
  );
  const { eventOptions, milestoneEvents } = useMilestonesEvents();
  const timeRange = useCommonInfoStore((s) => s.timeRange);
  const defaultInterval = useCommonInfoStore((s) => s.defaultInterval);
  const customerId = useCommonInfoStore((s) => s.userInfo.customer_id);
  useEffect(() => {
    if (defaultInterval.length > 0 && !interval?.length) {
      setInterval(defaultInterval);
    }
  }, [defaultInterval, setInterval, interval]);

  const timeout_ms = useMemo(() => {
    return applied.timeout * TIMEOUT_COEFFICIENTS[applied.timeoutUnit];
  }, [applied.timeout, applied.timeoutUnit]);

  // Ensure we always have a valid interval (fallback to defaultInterval)
  const safeInterval = interval || defaultInterval;

  const eventOptionsWithPopover = useMemo(() => {
    return eventOptions.map((event) => {
      return {
        ...event,
        label: (
          <EventShowcase
            name={event.label}
            popoverProps={{
              getPopupContainer: () => document.getElementById('journey-page') || document.body,
              overlayStyle: { zIndex: 9999 },
            }}
          />
        ),
        name: event.label,
      };
    });
  }, [eventOptions]);

  const handleIntervalChange = (value: [string, string]) => {
    setInterval(value);
  };
  const trimInvalidEvents = (events: string[] | string) => {
    if (Array.isArray(events)) {
      const validEvents = events.filter((event) =>
        eventOptions.some((option) => option.value === event),
      );
      return validEvents.length > 0 ? validEvents : undefined;
    }
    return eventOptions.find((option) => option.value === events) ? events : undefined;
  };
  const userJourneysPayload = useMemo<UserJourneyFilterRequest | undefined>(() => {
    const startWithCriticalEvent = trimInvalidEvents(applied.startEvents) as string[];
    const endWithCriticalEvent = trimInvalidEvents(applied.businessGoal) as string;
    const includeCriticalEvent = trimInvalidEvents(applied.includeEvents) as string[];
    const excludeCriticalEvent = trimInvalidEvents(applied.excludeEvents) as string[];
    const hiddenCriticalEvent = trimInvalidEvents(applied.hiddenEvents) as string[];
    const payload: UserJourneyFilterRequest = {
      time_range: interval,
      start_events: startWithCriticalEvent,
      end_event: endWithCriticalEvent,
      include_events_logic:
        includeCriticalEvent?.length > 0
          ? applied.includeMatchType === 'every'
            ? 'every'
            : 'any'
          : undefined,
      include_events: includeCriticalEvent,
      hidden_events: hiddenCriticalEvent,
      exclude_events: excludeCriticalEvent,
      metadata: globalFilter && globalFilter?.length > 0 ? globalFilter : undefined,
      limit: applied.topPaths || 15,
      timeout_ms: timeout_ms,
    };

    if (!payload.start_events && !payload.end_event) {
      return undefined;
    }

    return payload;
  }, [interval, applied, globalFilter, timeout_ms]);

  const {
    data: userJourneysData,
    isFetching: isLoadingJourneys,
    error: userJourneysError,
  } = usePostUserJourneysFilter(userJourneysPayload, {
    enabled: !!userJourneysPayload && displayType === DisplayType.SANKEY,
  });

  // Patterns payload
  const patternsPayload = useMemo<PatternsRequest | undefined>(() => {
    const endWithCriticalEvent = trimInvalidEvents(applied.businessGoal) as string;
    const startWithCriticalEvent = trimInvalidEvents(applied.startEvents) as string[];
    const includeCriticalEvent = trimInvalidEvents(applied.includeEvents) as string[];
    const excludeCriticalEvent = trimInvalidEvents(applied.excludeEvents) as string[];
    const payload: PatternsRequest = {
      time_range: interval,
      filters: globalFilter,
      include_events_logic:
        includeCriticalEvent?.length > 0
          ? applied.includeMatchType === 'every'
            ? 'every'
            : 'any'
          : undefined,
      start_with_critical_event: startWithCriticalEvent,
      end_with_critical_event: endWithCriticalEvent,
      include_critical_event: includeCriticalEvent,
      exclude_critical_event: excludeCriticalEvent,
      limit: applied.topPaths > 10 ? 10 : applied.topPaths,
    };
    return payload;
  }, [interval, globalFilter, applied]);

  const {
    data: patternsData,
    isFetching: isLoadingPatterns,
    error: patternsError,
  } = useGetPatterns(patternsPayload, {
    enabled: !!patternsPayload && displayType === DisplayType.PATTERNS,
  });

  // Handle patterns data when it arrives
  useEffect(() => {
    if (patternsData) {
      setPatterns(patternsData);
    }
  }, [patternsData, setPatterns]);

  // Handler for closing the GenerateJourneyModal
  const handleCloseGenerateJourneyModal = () => {
    setShowGenerateJourneyModal(false);
    appTracker({
      name: 'Journey Explorer Generate Journey Modal Close',
      data: {
        showGenerateJourneyModal: false,
      },
    });
  };

  // Handler for when user generates journey from modal
  const handleGenerateJourney = (selectedEventId: string) => {
    updateDraft({ businessGoal: selectedEventId });
    applyConfig(); // Apply immediately when generating from modal
    setShowGenerateJourneyModal(false);
    setDisplayType(DisplayType.SANKEY);
    appTracker({
      name: 'Journey Explorer Generate Journey Modal Generate',
      data: {
        selectedEventId: selectedEventId,
      },
    });
  };

  useEffect(() => {
    if (state?.milestone) {
      updateDraft({ businessGoal: state.milestone });
      applyConfig();
    }
  }, [state, updateDraft, applyConfig]);

  useEffect(() => {
    if (displayType !== DisplayType.SANKEY) {
      return;
    }
    if (applied.businessGoal && applied.startEvents?.length > 0) {
      setSankeyAlign('justify');
      return;
    }
    if (applied.startEvents?.length > 0) {
      setSankeyAlign('left');
      return;
    }
    if (applied.businessGoal) {
      setSankeyAlign('right');
      return;
    }
    setSankeyAlign('left');
  }, [displayType, applied.businessGoal, applied.startEvents]);

  const highlightedNodes = useMemo(() => {
    if (highlightedNodesFromProps?.length) {
      return highlightedNodesFromProps;
    } else {
      return [
        ...applied.startEvents,
        ...applied.includeEvents,
        ...applied.excludeEvents,
        applied.businessGoal,
      ].filter(Boolean);
    }
  }, [
    applied.businessGoal,
    applied.excludeEvents,
    highlightedNodesFromProps,
    applied.includeEvents,
    applied.startEvents,
  ]);
  const sortedData = useMemo(() => {
    return userJourneysData?.data?.sort((a, b) => b.score - a.score);
  }, [userJourneysData]);

  // Handle user journeys data when it arrives
  useEffect(() => {
    if (userJourneysData?.data) {
      const allpaths = sortedData.map((path) => path.user_path);
      setPaths(allpaths);
    }
  }, [sortedData, setPaths]);

  const sankeyData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) {
      return { nodes: [], links: [] };
    }
    // const sortedPaths = paths.sort((a, b) => b.score - a.score);
    return generateSankeyData(sortedData, !!applied.businessGoal, applied.startEvents.length > 0);
  }, [sortedData, applied.businessGoal, applied.startEvents.length]);

  const noData = useMemo(() => {
    if (isLoadingJourneys) {
      return false;
    }
    if (!applied.businessGoal && !applied.startEvents.length) {
      return true;
    }
    return sankeyData && sankeyData.nodes && sankeyData.nodes.length === 0;
  }, [sankeyData, applied.businessGoal, applied.startEvents, isLoadingJourneys]);
  const noPattern = useMemo(() => {
    return !patterns?.length;
  }, [patterns]);

  const handleNodeLabelClick = (node) => {
    onNodeLabelClick?.(node);
    const { name: nodeName } = node;
    const eventId = milestoneEvents.find((event) => event.name === nodeName)?.id;
    if (!eventId) {
      return;
    }
    if (selectedNodes.includes(eventId)) {
      setSelectedNodes(selectedNodes.filter((i) => i !== eventId));
    } else {
      setSelectedNodes([...selectedNodes, eventId]);
    }
    appTracker({
      name: 'Journey Explorer Node Label Click',
      data: {
        nodeName: nodeName,
        eventId: eventId,
        topPaths: applied.topPaths,
        globalFilter: globalFilter?.map((i) => `${i.keys}:${i.values}`).join(','),
        selectedNodes: selectedNodes.join(','),
      },
    });
  };

  const { width, height } = useMemo(() => {
    return getSankeySize(sankeyData, 'right');
  }, [sankeyData]);

  useEffect(() => {
    appTracker({
      name: 'Journey Explorer Page Load',
      data: {},
    });
  }, []);

  const resetAllData = () => {
    resetStore();
    appTracker({
      name: 'Journey Explorer Reset All Data',
      data: {},
    });
  };

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (id) {
      getShareConfig(id)
        .then((res) => {
          const { filter_config } = res;
          if (filter_config) {
            // Convert timeout_ms to unit and value
            let timeoutVal = filter_config.timeout_ms / 1000;
            let timeoutUnitVal = 's';
            if (filter_config.timeout_ms % (3600 * 1000) === 0) {
              timeoutVal = filter_config.timeout_ms / (3600 * 1000);
              timeoutUnitVal = 'h';
            } else if (filter_config.timeout_ms % (60 * 1000) === 0) {
              timeoutVal = filter_config.timeout_ms / (60 * 1000);
              timeoutUnitVal = 'm';
            }

            // Update draft with all config values
            updateDraft({
              businessGoal: filter_config.end_event,
              startEvents: filter_config.start_events,
              includeEvents: filter_config.include_events,
              excludeEvents: filter_config.exclude_events,
              hiddenEvents: filter_config.hidden_events,
              topPaths: filter_config.limit,
              timeout: timeoutVal,
              timeoutUnit: timeoutUnitVal,
              includeMatchType: filter_config.include_events_logic || 'any',
            });

            // Apply immediately for shared config
            applyConfig();

            setGlobalFilter(filter_config.metadata);
            setDisplayType(DisplayType.SANKEY);
            setSelectedNodes([]);

            if (filter_config.time_range) {
              setInterval(filter_config.time_range);
            }

            setShowGenerateJourneyModal(false);
          }
        })
        .catch((err) => {
          console.error('Failed to load share config', err);
        });
    }
  }, [id]);

  const trimInvalidSelectedNodes = (trimInvalidEvents(selectedNodes) as string[]) || [];

  const showResetButton = useMemo(() => {
    return !isEqual(DEFAULT_CONFIG, draft);
  }, [draft]);

  return (
    <div id="journey-page">
      <HeaderContainer>
        <GlobalFilter
          interval={interval}
          style={{ flex: 1 }}
          globalFilter={globalFilter}
          onFilterChange={(filter) => {
            setGlobalFilter(filter);
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            alignSelf: 'flex-start',
          }}
        >
          {showResetButton && (
            <Button
              variant="outlined"
              color="orange"
              iconPosition="start"
              style={{ border: 0, background: '#FFF7E6' }}
              icon={<RedoOutlined />}
              onClick={() => {
                resetAllData();
                if (id) {
                  window.history.replaceState(null, '', `/app/pattern-analytics/paths`);
                }
              }}
            >
              Reset
            </Button>
          )}
          <DatePicker
            value={interval}
            range={timeRange}
            maxLength={
              customerId === 1960188863
                ? 30 * 24 * 60 * 60 * 1000
                : customerId === 1960188707
                  ? 7 * 24 * 60 * 60 * 1000
                  : 3 * 24 * 60 * 60 * 1000
            }
            onChange={handleIntervalChange}
          />
          <SharePopover />
          {/* <Segmented
            value={displayType}
            onChange={(value) => {
              setDisplayType(value as DisplayType);
              if (value === DisplayType.PATTERNS && draft.topPaths > DEFAULT_TOP_PATHS) {
                updateDraft({ topPaths: DEFAULT_TOP_PATHS });
              }
              // tracking data
              appTracker({
                name: 'Explorer Type Changed',
                data: {
                  displayType: value,
                  milestone: applied.businessGoal,
                },
              });
            }}
            options={[
              { label: 'Paths', value: DisplayType.SANKEY },
              { label: 'Patterns', value: DisplayType.PATTERNS },
            ]}
          /> */}
        </div>
      </HeaderContainer>
      <PageContainer>
        <ResizablePanel
          defaultSize={550}
          maxSize={550}
          left={
            <LeftPanel>
              <PanelHeaderForLeft>
                <span className="title">Configurations</span>
                <Button
                  variant="outlined"
                  color="primary"
                  iconPosition="end"
                  icon={<CaretRightOutlined />}
                  disabled={!changed}
                  style={{ paddingRight: 8 }}
                  onClick={() => void handleApply()}
                >
                  Apply
                </Button>
              </PanelHeaderForLeft>
              <LeftFilters
                displayType={displayType}
                eventOptions={eventOptionsWithPopover}
                businessGoal={draft.businessGoal}
                setBusinessGoal={(v) => updateDraft({ businessGoal: v })}
                selectedNodes={selectedNodes}
                setSelectedNodes={setSelectedNodes}
                startEvents={draft.startEvents}
                setStartEvents={(v) => updateDraft({ startEvents: v })}
                includeMatchType={draft.includeMatchType}
                setIncludeMatchType={(v) => updateDraft({ includeMatchType: v })}
                includeEvents={draft.includeEvents}
                setIncludeEvents={(v) => updateDraft({ includeEvents: v })}
                excludeEvents={draft.excludeEvents}
                setExcludeEvents={(v) => updateDraft({ excludeEvents: v })}
                topPaths={draft.topPaths}
                setTopPaths={(v) => updateDraft({ topPaths: v })}
                timeout={draft.timeout}
                setTimeout={(v) => updateDraft({ timeout: v })}
                timeoutUnit={draft.timeoutUnit}
                setTimeoutUnit={(v) => updateDraft({ timeoutUnit: v })}
                hiddenEvents={draft.hiddenEvents}
                setHiddenEvents={(v) => updateDraft({ hiddenEvents: v })}
              />
            </LeftPanel>
          }
          right={
            <>
              {displayType === DisplayType.SANKEY && (
                <ChartContainer>
                  <PanelHeader>
                    <span className="title">Top Paths for Critical Events: </span>
                    <div className="description">
                      {applied.startEvents
                        .map((node) => milestoneEvents.find((event) => event.id === node)?.name)
                        .join(', ')}
                      {applied.startEvents.length > 0 && ', '}
                      {milestoneEvents.find((event) => event.id === applied.businessGoal)?.name}
                    </div>
                  </PanelHeader>
                  <ChartContentContainer>
                    <FloatPanel>
                      <span className="title">Select events to view conversion details: </span>
                      <div className="description">
                        <SelectedEventsList>
                          {trimInvalidSelectedNodes?.length > 0 &&
                            trimInvalidSelectedNodes.map((node) => (
                              <SelectedEventTag
                                key={node}
                                closable
                                onClose={() => {
                                  setSelectedNodes(
                                    trimInvalidSelectedNodes.filter((n) => n !== node),
                                  );
                                }}
                              >
                                {milestoneEvents.find((event) => event.id === node)?.name}
                              </SelectedEventTag>
                            ))}
                          {trimInvalidSelectedNodes?.length > 0 && (
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                setSelectedNodes([]);
                              }}
                            >
                              Clear All
                            </Button>
                          )}
                        </SelectedEventsList>
                      </div>
                    </FloatPanel>
                    <Spin spinning={isLoadingJourneys} tip="Loading Paths...">
                      <div style={{ height: 'calc(100vh - 250px)' }}>
                        {!noData ? (
                          <Sankey
                            data={sankeyData}
                            width={width}
                            height={height}
                            align={sankeyAlign}
                            highlightedNodes={highlightedNodes}
                            selectedNodes={selectedNodes}
                            onNodeLabelClick={handleNodeLabelClick}
                          />
                        ) : (
                          <NoData
                            type="journey"
                            message={
                              !applied.businessGoal && !applied.startEvents.length
                                ? 'Select a Milestone or Start With event from the left panel to begin creating Paths.'
                                : undefined
                            }
                          />
                        )}
                      </div>
                    </Spin>
                  </ChartContentContainer>
                </ChartContainer>
              )}
              {displayType === DisplayType.PATTERNS && (
                <ChartContainer>
                  <MetricChart
                    filters={globalFilter || []}
                    milestone={applied.businessGoal}
                    interval={safeInterval}
                  />
                  {noPattern ? (
                    <ChartContentContainer>
                      <NoData type="patterns" />
                    </ChartContentContainer>
                  ) : (
                    <>
                      <TopBarOfPatterns>
                        {applied.businessGoal ? (
                          <>
                            Top Conversion Patterns to Milestone:
                            <BusinessGoal>
                              {
                                milestoneEvents.find((event) => event.id === applied.businessGoal)
                                  ?.name
                              }
                            </BusinessGoal>
                          </>
                        ) : (
                          <span>Top Patterns</span>
                        )}
                      </TopBarOfPatterns>
                      <FunnelList
                        isLoadingPatterns={isLoadingPatterns}
                        interval={safeInterval}
                        globalFilter={globalFilter || []}
                      />
                    </>
                  )}
                </ChartContainer>
              )}
            </>
          }
          styles={{
            left: { background: '#fff' },
          }}
        />
      </PageContainer>
      {displayType === DisplayType.SANKEY && (
        <MiniFunnel
          steps={selectedNodes}
          filters={globalFilter || []}
          topPaths={applied.topPaths}
          interval={safeInterval}
          timeout={applied.timeout}
          timeoutUnit={applied.timeoutUnit}
        />
      )}
      <GenerateJourneyModal
        eventOptions={eventOptionsWithPopover}
        visible={showGenerateJourneyModal && !id}
        onClose={handleCloseGenerateJourneyModal}
        onGenerate={handleGenerateJourney}
      />
    </div>
  );
};

export default JourneyPage;
